import { EntityType } from '@crew/enums/domain'
import {
  useProjectAdvancedSettings,
  useProjectPermissions,
  useValueChangeEffect,
} from '@crew/hooks'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { TaskEntryDialog } from 'features/task/components/taskEntryDialog/taskEntryDialog'
import { FC, Fragment, memo, useCallback, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ProjectDetailTaskSearch } from './components/projectDetailTaskSearch/projectDetailTaskSearch'
import { useTranslation } from '@crew/modules/i18n'
import { TaskDetailListTabs, TaskDisplayModeButtonGroup } from 'enums/app'
import { useModal } from 'components/layouts/modal/useModal'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import {
  setIsShowEditWBS,
  setSelectedTaskKanbanBucketType,
  setSelectedTaskListDisplayMode,
} from 'features/project/components/projectDetailPage/states/projectDetailSlice'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { getDefaultTabValue } from '@crew/utils/enum'
import { CrewConfirmDialog } from 'components/elements/crewConfirmDialog/crewConfirmDialog'
import { CrewButtonGroup } from 'components/elements/crewButtonGroup/crewButtonGroup'
import { TaskKanbanBucketType } from '@crew/enums/app'
import { CrewSelectBox } from 'components/devextreme/crewSelectBox'
import { CrewGuideToolTip } from 'components/devextreme/crewGuideToolTip/crewGuideToolTip'
import { Menu, Transition } from '@headlessui/react'
import MoreHoriz from '~icons/material-symbols/more-horiz'
import classNames from 'classnames'
import qs from 'qs'
import { apiBaseUrl } from '@crew/configs/constants'
import { ExportProjectTasksCsvRequest } from '@crew/apis/project/models/exportProjectTasksCsv/request'
import {
  downloadBlob,
  buildApiUrl,
  extractFileNameFromContentDisposition,
} from '@crew/utils'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { axiosInstance } from 'apis/axiosInstance'
import { AxiosError } from 'axios'
import { TaskEntryFormMode } from 'features/task/components/taskEntryDialog/components/taskEntryForm/taskEntryForm'
import { TaskImportCsvDialog } from 'features/task/components/taskImportCsvDialog/taskImportCsvDialog'
import { TaskEntryByTemplateDialog } from 'features/task/components/taskEntryByTemplateDialog/taskEntryByTemplateDialog'
import { usePaidFeatureStatus } from 'hooks/usePaidFeatureStatus'
import { CrewPaidPlanFeatureMenuItem } from 'components/elements/crewPaidPlanFeatureMenuItem/crewPaidPlanFeatureMenuItem'

type ButtonGroupInfo = {
  id: string
  text: string | undefined
  icon: JSX.Element | undefined
}

export const ProjectDetailTaskListToolbar: FC = memo(() => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { navigate } = useCrewNavigate()
  const { projectId } = useParams()

  // projectIdが存在しない場合は後続の処理が行えないのでエラーとする
  if (!projectId) {
    throw new Error('projectId is undefined')
  }

  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
    useModal()

  const [showApiErrors] = useShowApiErrors()

  // 検索パラメータ
  const [searchParams] = useSearchParams()
  const queryParams = useMemo(
    () => qs.parse(searchParams.toString()),
    [searchParams]
  )

  /** ダイアログ */
  // タスク登録ダイアログ
  const [
    isProjectDetailTaskEntryDialogOpen,
    openProjectDetailTaskEntryDialog,
    closeProjectDetailTaskEntryDialog,
  ] = useModal()

  // タスクCSVインポートダイアログ
  const [
    isImportTaskCsvDialogOpen,
    openImportTaskCsvDialog,
    closeImportTaskCsvDialog,
  ] = useModal()

  // タスクテンプレートから登録ダイアログ
  const [
    isTaskEntryByTemplateDialogOpen,
    openTaskEntryByTemplateDialog,
    closeTaskEntryByTemplateDialog,
  ] = useModal()

  // 選択中の表示形式
  const selectedTaskListDisplayMode = useAppSelector(
    (state) => state.projectDetail.selectedTaskListDisplayMode
  )

  const isShowEditWBS = useAppSelector(
    (state) => state.projectDetail.isShowEditWBS
  )

  const isWbsNumberRenumbering = useAppSelector(
    (state) => state.projectDetail.isWbsNumberRenumbering
  )

  const selectedTaskKanbanBucketType = useAppSelector(
    (state) => state.projectDetail.selectedTaskKanbanBucketType
  )

  const { hasPrjTaskCreatePermission } = useProjectPermissions(
    EntityType.Project,
    projectId
  )

  // 有償機能が利用可能かどうかを判定する
  const paidFeatureStatus = usePaidFeatureStatus()

  const { doProjectManagement } = useProjectAdvancedSettings(projectId)

  const taskKanbanBucketTypeDataSource = useMemo(
    () =>
      Object.values(TaskKanbanBucketType).map((item) => {
        return { id: item.value, name: t(item.text) }
      }),
    [t]
  )

  // プロジェクトの設定でプロジェクト管理がOFFの場合、ガントチャート、EVMを非表示にする
  useValueChangeEffect(
    () => {
      if (
        !doProjectManagement &&
        (selectedTaskListDisplayMode === TaskDisplayModeButtonGroup.Gantt.id ||
          selectedTaskListDisplayMode === TaskDisplayModeButtonGroup.Evm.id)
      ) {
        dispatch(
          setSelectedTaskListDisplayMode(TaskDisplayModeButtonGroup.List.id)
        )
      }
    },
    [dispatch, doProjectManagement, selectedTaskListDisplayMode],
    doProjectManagement
  )

  // 表示形式のボタングループ
  const taskDisplayModeButtonGroup: ButtonGroupInfo[] = useMemo(() => {
    const items = Object.keys(TaskDisplayModeButtonGroup)
      .filter((key) => {
        const item =
          TaskDisplayModeButtonGroup[
            key as keyof typeof TaskDisplayModeButtonGroup
          ]

        // project management is OFF then hide Gantt chart, EVM
        if (
          item.id === TaskDisplayModeButtonGroup.Gantt.id ||
          item.id === TaskDisplayModeButtonGroup.Evm.id
        ) {
          return doProjectManagement
        }

        return true
      })
      .map((key) => {
        const item =
          TaskDisplayModeButtonGroup[
            key as keyof typeof TaskDisplayModeButtonGroup
          ]

        const SvgIcon = item.icon

        return {
          id: item.id,
          text: undefined,
          icon: (
            <div id={`${item.id}`}>
              <SvgIcon width={24} height={24} />
              {/* ツールチップ */}
              <CrewGuideToolTip
                target={`#${item.id}`}
                position="bottom"
                type={item.tooltipType}
              />
            </div>
          ),
        }
      })

    return items
  }, [doProjectManagement])

  // 表示形式の切替ボタンクリック時
  const handleTaskDisplayModeItemClick = useCallback(
    (itemData: ButtonGroupInfo) => {
      // 表示形式を更新
      dispatch(setSelectedTaskListDisplayMode(itemData.id))
    },
    [dispatch]
  )

  // タスク登録完了
  const handleTaskRegistered = useCallback(
    (taskId: string) => {
      // タスク詳細画面に遷移
      navigate(`/tasks/${taskId}/${getDefaultTabValue(TaskDetailListTabs)}`)

      //close project detail task entry dialog
      closeProjectDetailTaskEntryDialog()
    },
    [navigate, closeProjectDetailTaskEntryDialog]
  )

  // show edit WBS
  const handleEditWBSButtonClick = useCallback(() => {
    dispatch(setIsShowEditWBS(true))
  }, [dispatch])

  // finish editing WBS
  const handleFinishEditingWBSButtonClick = useCallback(() => {
    if (isWbsNumberRenumbering) {
      openConfirmDialog()
    } else {
      dispatch(setIsShowEditWBS(false))
    }
  }, [dispatch, isWbsNumberRenumbering, openConfirmDialog])

  // 確認ダイアログの許可ボタンクリック時のイベントハンドラ
  const handlePermitButtonClick = useCallback(() => {
    dispatch(setIsShowEditWBS(false))
    closeConfirmDialog()
  }, [closeConfirmDialog, dispatch])

  // カンバン表示モードのバケットタイプ変更
  const handleTaskKanbanBucketTypeChange = useCallback(
    (value: string) => {
      dispatch(setSelectedTaskKanbanBucketType(value))
    },
    [dispatch]
  )

  // テンプレートから登録クリック時のイベントハンドラ
  const handleInsertTaskFromTemplateButtonClick = useCallback(async () => {
    // 有償機能が使えない場合は処理しない
    if (!paidFeatureStatus.canUsePaidFeature) return

    // タスクテンプレートロードダイアログを開く
    openTaskEntryByTemplateDialog()
  }, [openTaskEntryByTemplateDialog, paidFeatureStatus.canUsePaidFeature])

  // CSVインポートクリック時のイベントハンドラ
  const handleImportCsvButtonClick = useCallback(async () => {
    // 有償機能が使えない場合は処理しない
    if (!paidFeatureStatus.canUsePaidFeature) return

    // タスクCSVインポートダイアログを開く
    openImportTaskCsvDialog()
  }, [paidFeatureStatus.canUsePaidFeature, openImportTaskCsvDialog])

  // CSVエクスポートクリック時のイベントハンドラ
  const handleExportCsvButtonClick = useCallback(async () => {
    try {
      // プロジェクトIDが存在しない場合は適切に処理できないので中断する
      if (!projectId) return

      // 有償機能が使えない場合は処理しない
      if (!paidFeatureStatus.canUsePaidFeature) return

      // バックエンドへのリクエストパラメータを作成
      const requestData: ExportProjectTasksCsvRequest = {
        projectId: projectId,
        keyword: queryParams.keyword as string | undefined,
        taskKindIds: queryParams.taskKindIds as string[] | undefined,
        assignToUser: queryParams.assignToUser as string | undefined,
        taskStateIds: queryParams.taskStateIds as string[] | undefined,
        taskPriorities: (queryParams.taskPriorities
          ? (Array.isArray(queryParams.taskPriorities)
              ? queryParams.taskPriorities
              : [queryParams.taskPriorities]
            ).map((value) => Number(value))
          : undefined) as number[] | undefined,
        taskCategoryIds: queryParams.taskCategoryIds as string[] | undefined,
        startDate: queryParams.startDate as string | undefined,
        dueDate: queryParams.dueDate as string | undefined,
        createdById: queryParams.createdById as string | undefined,
        updatedById: queryParams.updatedById as string | undefined,
        createdAt: queryParams.createdAt as string | undefined,
        updatedAt: queryParams.updatedAt as string | undefined,
        targetDateFrom: queryParams.targetDateFrom as string | undefined,
        targetDateTo: queryParams.targetDateTo as string | undefined,
        taskStateTypes: queryParams.taskStateTypes as string[] | undefined,
        taskIds: undefined,
      }

      // RTK Queryではなく、axiosInstanceを使用し、fetchを行う
      // RTKQueryは通常JSONレスポンスを想定しており、Blob（バイナリデータ）を適切にシリアライズできないため
      // 開発者ツールのコンソールを見ると、エラー（A non-serializable value was detected.～）が出ている
      const params = qs.stringify(requestData, { arrayFormat: 'repeat' })
      const url = buildApiUrl(
        apiBaseUrl(),
        `api/v1/projects/${projectId}/tasks/export-csv`
      )
      url.search = params.toString()

      // TODO: 呼び出し側でaxiosInstanceを直接使用しないように、以下タスクでリファクタ予定
      // https://break-tmc.atlassian.net/browse/CREW-16919
      const response = await axiosInstance.get(url.toString(), {
        responseType: 'blob',
      })
      const blob = response.data
      const contentDisposition = response.headers['content-disposition']

      const fileName =
        extractFileNameFromContentDisposition(contentDisposition) ||
        'export_project_tasks.csv'

      // Blobを使用してダウンロードを実行
      downloadBlob(blob, fileName)
    } catch (error: unknown) {
      // AxiosErrorかどうかをチェック
      if (error instanceof AxiosError) {
        // エラーレスポンスからエラーbodyを取得
        const errorData = error.response?.data
        const errorStatus = error.response?.status

        // エラーデータがBlobの場合（responseType: 'blob'のため）
        if (errorData instanceof Blob) {
          // BlobをJSONに変換してエラー内容を確認
          try {
            const text = await errorData.text()
            const errorJson = JSON.parse(text)
            const errorObject = {
              status: errorStatus,
              data: errorJson,
            }
            showApiErrors(errorObject)
          } catch (parseError) {
            console.error('Error parsing blob as JSON:', parseError)
          }
        }
      } else {
        // その他の予期しないエラーはログに出力しておくのみとする
        console.error('Unexpected error:', error)
      }
    }
  }, [
    paidFeatureStatus.canUsePaidFeature,
    projectId,
    queryParams.assignToUser,
    queryParams.createdAt,
    queryParams.createdById,
    queryParams.dueDate,
    queryParams.keyword,
    queryParams.startDate,
    queryParams.targetDateFrom,
    queryParams.targetDateTo,
    queryParams.taskCategoryIds,
    queryParams.taskKindIds,
    queryParams.taskPriorities,
    queryParams.taskStateIds,
    queryParams.taskStateTypes,
    queryParams.updatedAt,
    queryParams.updatedById,
    showApiErrors,
  ])

  // ツールバーアイテムの描画
  const renderToolbarItems = useCallback(() => {
    // WBSを編集/WBSの編集終了
    const renderEditWBSButton = () => {
      return (
        <CrewButton
          type="normal"
          stylingMode="outlined"
          onClick={
            isShowEditWBS
              ? handleFinishEditingWBSButtonClick
              : handleEditWBSButtonClick
          }
          text={t(isShowEditWBS ? 'action.finishEditingWBS' : 'action.editWBS')}
        />
      )
    }

    // メニューボタン
    const renderMenuButton = () => (
      <div className="flex flex-row items-center">
        <Menu as="div" className="relative inline-block text-left">
          <div>
            <Menu.Button className="flex items-center rounded-md">
              <CrewButton
                icon={<MoreHoriz width={20} height={20} />}
                type="normal"
                stylingMode="outlined"
              />
            </Menu.Button>
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            {/* メニューの位置を固定し、一番上に表示する必要があるため、absoluteとz-30(※)を指定している。
                ※本コンポーネントの下に表示しているCrewTableのヘッダがz-20のため、それを上回る値としてz-30を指定。
             */}
            <Menu.Items className="absolute right-0 z-30 mt-2 w-48 origin-top-right rounded-md crew-bg-default crew-text-default shadow-lg ring-1 ring-crew-gray-200 dark:ring-crew-gray-700 focus:outline-none">
              <div className="py-1">
                {/* テンプレートから登録 */}
                <Menu.Item>
                  {({ active }) => (
                    <div
                      className={classNames(
                        { 'crew-bg-gray-2': active },
                        'block text-sm',
                        { 'opacity-50': !paidFeatureStatus.canUsePaidFeature }
                      )}
                    >
                      <CrewPaidPlanFeatureMenuItem
                        isPaidFeatureEnabled={
                          paidFeatureStatus.canUsePaidFeature
                        }
                        isPaidFeatureTrial={paidFeatureStatus.isTrialPeriod}
                        label={t('label.registerByTemplate')}
                        onClick={handleInsertTaskFromTemplateButtonClick}
                        className="w-full px-4 py-2"
                      />
                    </div>
                  )}
                </Menu.Item>
                {/* CSVインポート */}
                <Menu.Item>
                  {({ active }) => (
                    <div
                      className={classNames(
                        { 'crew-bg-gray-2': active },
                        'block text-sm',
                        { 'opacity-50': !paidFeatureStatus.canUsePaidFeature }
                      )}
                    >
                      <CrewPaidPlanFeatureMenuItem
                        isPaidFeatureEnabled={
                          paidFeatureStatus.canUsePaidFeature
                        }
                        isPaidFeatureTrial={paidFeatureStatus.isTrialPeriod}
                        label={t('label.importCsv')}
                        onClick={handleImportCsvButtonClick}
                        className="w-full px-4 py-2"
                      />
                    </div>
                  )}
                </Menu.Item>
                {/* CSVエクスポート */}
                <Menu.Item>
                  {({ active }) => (
                    <div
                      className={classNames(
                        { 'crew-bg-gray-2': active },
                        'block text-sm',
                        { 'opacity-50': !paidFeatureStatus.canUsePaidFeature }
                      )}
                    >
                      <CrewPaidPlanFeatureMenuItem
                        isPaidFeatureEnabled={
                          paidFeatureStatus.canUsePaidFeature
                        }
                        isPaidFeatureTrial={paidFeatureStatus.isTrialPeriod}
                        label={t('label.exportCsv')}
                        onClick={handleExportCsvButtonClick}
                        className="w-full px-4 py-2"
                      />
                    </div>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    )

    // 新規タスクボタン
    const renderNewTaskButton = () => (
      <CrewButton
        id="project-detail-task-list-toolbar-new-task-button"
        type="primary"
        onClick={openProjectDetailTaskEntryDialog}
        text={t('action.newTask')}
      />
    )

    // カンバン表示モードのアイテムを描画
    const renderKanbanModeItems = () => (
      <>
        <CrewSelectBox
          dataSource={taskKanbanBucketTypeDataSource}
          valueExpr="id"
          displayExpr="name"
          searchEnabled={false}
          showClearButton={false}
          minSearchLength={0}
          onValueChange={handleTaskKanbanBucketTypeChange}
          value={selectedTaskKanbanBucketType}
        />
        {hasPrjTaskCreatePermission && renderNewTaskButton()}
      </>
    )

    // タスクリスト表示モードによってツールバーアイテムを変更
    if (selectedTaskListDisplayMode === TaskDisplayModeButtonGroup.Gantt.id) {
      return hasPrjTaskCreatePermission ? (
        <div className="flex flex-row items-center gap-x-1">
          {renderEditWBSButton()}
        </div>
      ) : null
    }
    // カンバン表示モードの場合、新規タスクボタンを表示
    else if (
      selectedTaskListDisplayMode === TaskDisplayModeButtonGroup.Kanban.id
    ) {
      return (
        <div className="flex flex-row items-center gap-x-1">
          {renderKanbanModeItems()}
          {renderMenuButton()}
        </div>
      )
    } else {
      // その他の表示モードの場合
      if (hasPrjTaskCreatePermission) {
        return (
          <div className="flex flex-row items-center gap-x-1">
            {renderNewTaskButton()}
            {renderMenuButton()}
          </div>
        )
      } else {
        return null
      }
    }
  }, [
    handleEditWBSButtonClick,
    handleExportCsvButtonClick,
    handleFinishEditingWBSButtonClick,
    handleImportCsvButtonClick,
    handleInsertTaskFromTemplateButtonClick,
    handleTaskKanbanBucketTypeChange,
    hasPrjTaskCreatePermission,
    isShowEditWBS,
    openProjectDetailTaskEntryDialog,
    paidFeatureStatus.canUsePaidFeature,
    paidFeatureStatus.isTrialPeriod,
    selectedTaskKanbanBucketType,
    selectedTaskListDisplayMode,
    t,
    taskKanbanBucketTypeDataSource,
  ])

  return (
    <>
      <div className="flex-shrink-0">
        <div className="flex p-2.5 justify-between">
          {/* 表示切替ボタングループ */}
          <CrewButtonGroup
            items={taskDisplayModeButtonGroup}
            keyExpr="id"
            textExpr="text"
            iconExpr="icon"
            stylingMode="text"
            selectedItemKey={selectedTaskListDisplayMode}
            onItemClick={handleTaskDisplayModeItemClick}
          />

          {/* ツールバーアイテム */}
          {renderToolbarItems()}
        </div>

        {/* 検索条件 */}
        {/* ガントチャート・EVMチャートに対して検索ボックスを表示しない */}
        {selectedTaskListDisplayMode !== TaskDisplayModeButtonGroup.Gantt.id &&
          selectedTaskListDisplayMode !== TaskDisplayModeButtonGroup.Evm.id && (
            <div className="px-2.5 pb-2.5">
              <ProjectDetailTaskSearch />
            </div>
          )}

        {/* タスク登録ダイアログ */}
        <TaskEntryDialog
          entryFormMode={TaskEntryFormMode.New}
          title={t('label.addTaskTitle')}
          onSubmit={handleTaskRegistered}
          isOpen={isProjectDetailTaskEntryDialogOpen}
          onClose={closeProjectDetailTaskEntryDialog}
          projectId={projectId}
        />

        {/* Dialog confirm end the edit order task */}
        <CrewConfirmDialog
          isOpen={isConfirmDialogOpen}
          message={t('message.task.wbsNumberReorderConfirm')}
          onPermitButtonClick={handlePermitButtonClick}
          onCancelButtonClick={closeConfirmDialog}
          permitButtonText={t('action.yes')}
          permitButtonTheme="normal"
          cancelButtonText={t('action.no')}
          cancelButtonTheme="primary"
        />
      </div>

      {/* タスクCSVインポートダイアログ */}
      <TaskImportCsvDialog
        title={t('label.importTaskTitle')}
        isOpen={isImportTaskCsvDialogOpen}
        onClose={closeImportTaskCsvDialog}
        projectId={projectId}
      />

      {/* タスクテンプレートから登録ダイアログ */}
      <TaskEntryByTemplateDialog
        isOpen={isTaskEntryByTemplateDialogOpen}
        onClose={closeTaskEntryByTemplateDialog}
        projectId={projectId}
        onSubmit={closeTaskEntryByTemplateDialog}
      />
    </>
  )
})
